import { useContext, useEffect } from 'react';
import { useQuery } from 'react-query';
import { mapNibud } from '../../components/pension-robot/expenses-overlay/NibudModal';
import { NIBUD_PENSION_RESULT_ENDPOINT, SECONDARY_NIBUD_API_KEY } from '../../config/nibud';
import { Car, PensionContext } from '../../contexts/pension-robot/PensionContext';
import { apifetch } from '../robotapi';

type NibudRequestParameters = {
    Hoofdpersonen: { Geslacht?: string }[],
    Autos: [
      {
        KilometersPerJaar: string,
        TypeAuto: string,
        StatusAuto: string,
        Nieuwwaarde?: number,
      }
    ],
    Woning: {
      TypeWoning: string,
      SoortWoning: string,
      WozWaarde: string,
      Bouwjaar?: string,
      Energielabel?: string,
      GemeenteId?: string,
      GemeenteCbsId?: string,
    },
    NettoBesteedbaarInkomenPerMaand: number,
}

type NibudPensionResultElement = {
    Uitgavepost: number,
    Bedrag: number
}

type NibudPensionResult = NibudPensionResultElement[]

const usePensionParametersToNibudRequestParameters = () => {
    const { parameters } = useContext(PensionContext);

    const getCarAsNibudParameters = (car: Car) => { 
        return {
            KilometersPerJaar: car.distance,
            TypeAuto: car.type,
            StatusAuto: car.carBoughtStatus,
            Nieuwwaarde: car.carValue,
        }
    }

    const getNetNibudIncome = () => {
        const selfNetSalary = parameters.personalInfo.self?.salary
        const partnerNetSalary = parameters.personalInfo.partner?.salary
        const calculatedTogether = parameters.personalInfo.isCalculatedTogether
        
        if (selfNetSalary === undefined)
            return undefined
        
        if (!calculatedTogether)
            return selfNetSalary / 100

        if (partnerNetSalary === undefined)        
            return undefined

        return ( selfNetSalary + partnerNetSalary )  / 100
    }

    const getPersons = () => {
        // For this implementation we don't use gender, which is the only entry for a person.
        // Still we need to send that it's for two persons, i.e. sending two empty objects
        if (parameters.personalInfo.isCalculatedTogether)
            return [{}, {}]
        return [{}]
    }

    return {
        Hoofdpersonen: getPersons(),
        // As mentioned by Andy of BND 08-12-2021, childeren are not taken into account for pension
        // Kinderen: [{}]
        Autos: parameters.expensesInfo?.nibud?.carInfo?.cars.map(getCarAsNibudParameters),
        Woning: {
          TypeWoning: parameters.expensesInfo?.nibud?.house?.houseType,
          SoortWoning: parameters.expensesInfo?.nibud?.house?.paymentCategory,
          WozWaarde: parameters.expensesInfo?.nibud?.house?.woz,
        //   Other variables, not taken into account for this application atm
        //   Bouwjaar: '',
        //   Energielabel: '',
        //   GemeenteId: '',
        //   GemeenteCbsId: '',
        },
        NettoBesteedbaarInkomenPerMaand: getNetNibudIncome()
    } as NibudRequestParameters
}


export const useGetNibudPensionResult = () => {
    const nibudPensionRequestBody = usePensionParametersToNibudRequestParameters();
    const { actions } = useContext(PensionContext);
    const endpoint = NIBUD_PENSION_RESULT_ENDPOINT;
    
    const nibudPensionResult = useQuery<NibudPensionResult>( endpoint,
        async () => {
            const getNibudPensionResult = apifetch<NibudPensionResult, NibudRequestParameters>(endpoint, "POST", {
                    "Ocp-Apim-Subscription-Key" : SECONDARY_NIBUD_API_KEY,
                },
            );
            const temp = getNibudPensionResult(nibudPensionRequestBody);
            return temp;
        },
        {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        }
    );

    useEffect(() => {
        if ( nibudPensionResult.data ){
            actions.setParameters({
                expensesInfo: {
                    nibud: {
                        calculatedExpenses: mapNibud(nibudPensionResult.data),
                    },
                },
            });
        }
    // "actions" should be not be part of the useEffect dependency array, as we use the PensionContext
    // and also set it in this useEffect. Setting it will cause a rerender of this component itself, 
    // and by setting it the memory adress of actions will change. Together that will cause an infinite 
    // loop ( PensionContext updates, so this hook will update, then the useEffect thinks actions changed
    // so it will again update PensionContext... etc). Therefore we use "eslint-disable-line".
    }, [nibudPensionResult.data]); // eslint-disable-line react-hooks/exhaustive-deps


    return nibudPensionResult;
};